import React, { useState, useEffect } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  makeStyles,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
} from "@material-ui/core";
import { Description as DescriptionIcon, CloudUpload as CloudUploadIcon, KeyboardBackspace } from "@material-ui/icons";
import DatePickerACCB from "../DatePickerACCB";
import { getDirections, createDirection, uploadDirectionDocument } from "../../service";
import { downloadLegalDoc } from "slices/disputeSlice";
import { useSnackbar } from "contexts/SnackbarContext";
import { DownloadFile } from "Clients/ACCB/filesACCB/FileApi";
import { navigateToTrustedUrl, openTrustedUrl } from "../../urlSafety";

const useStyles = makeStyles((theme) => ({
  paperCard: {
    padding: "2rem",
    border: "1px solid #E5E5E5",
    borderRadius: "4px",
    // boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.05)",
    marginBottom: "2rem",
    [theme.breakpoints.down("sm")]: {
      padding: "1rem",
    },
  },
  heading: {
    fontWeight: "600",
    fontSize: "1.5rem",
    lineHeight: "1.8rem",
    color: "black",
  },
  formContainer: {
    padding: "1rem",
    backgroundColor: "#FAFAFA",
    border: "1px solid #E5E5E5",
    borderRadius: "4px",
    // marginBottom: "2rem",
  },
  formTitle: {
    fontWeight: "600",
    fontSize: "1.1rem",
    marginBottom: "1rem",
  },
  tableHeaderCell: {
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    color: TEXT.strong,
    fontSize: "0.85rem",
    padding: "8px 12px",
  },
  tableCell: {
    fontSize: "0.85rem",
    padding: "8px 12px",
  },
  downloadLink: {
    textDecoration: "underline",
    cursor: "pointer",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
    fontSize: "0.85rem",
    wordBreak: "break-all",
  },
  submitButton: {
    textTransform: "none",
    fontWeight: 600,
    height: "40px",
  },
  uploadButton: {
    textTransform: "none",
    fontWeight: 600,
  },
  errorText: {
    fontSize: "0.85rem",
    color: TEXT.error,
  },
  emptyState: {
    padding: "3rem",
    textAlign: "center",
    color: TEXT.muted,
  },
  backButton: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textTransform: "none",
    padding: 0,
    minWidth: 0,
    marginBottom: "1rem",
  },
}));

// Date Parsing Helper
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr !== "string") return new Date(dateStr);
  if (dateStr.includes("/")) {
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
  }
  return new Date(dateStr);
};

// Date-only normalization helper
const getStartOfDay = (date) => {
  if (!date) return null;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Limitation status logic
const getLimitationStatus = (direction) => {
  const finalDate = getStartOfDay(parseDate(direction.final_date));
  if (!finalDate) return "—";

  if (direction.uploaded_date) {
    const uploadedDate = getStartOfDay(parseDate(direction.uploaded_date));
    if (uploadedDate <= finalDate) {
      return "Within Time";
    } else {
      return "Exceeded";
    }
  } else {
    const today = getStartOfDay(new Date());
    if (today > finalDate) {
      return "Pending (Overdue)";
    } else {
      return "Pending";
    }
  }
};

// Colored Chip Styles for Limitation
const getLimitationChipStyle = (status) => {
  switch (status) {
    case "Within Time":
      return { backgroundColor: "#ECFDF3", color: TEXT.success, fontWeight: 500, borderRadius: "4px" };
    case "Exceeded":
      return { backgroundColor: "#FEF3C7", color: TEXT.warning, fontWeight: 500, borderRadius: "4px" };
    case "Pending (Overdue)":
      return { backgroundColor: "#FEE2E2", color: TEXT.error, fontWeight: 500, borderRadius: "4px" };
    default:
      return { backgroundColor: "#F3F4F6", color: TEXT.strong, fontWeight: 500, borderRadius: "4px" };
  }
};

// Overdue Days calculation logic
const getOverdueDays = (direction) => {
  const finalDate = getStartOfDay(parseDate(direction.final_date));
  if (!finalDate) return "—";

  if (direction.uploaded_date) {
    const uploadedDate = getStartOfDay(parseDate(direction.uploaded_date));
    if (uploadedDate > finalDate) {
      const diffTime = uploadedDate.getTime() - finalDate.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  } else {
    const today = getStartOfDay(new Date());
    if (today > finalDate) {
      const diffTime = today.getTime() - finalDate.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  }
  return "—";
};

// Format Date to YYYY-MM-DD
const formatLocalDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function RecordOfDirections({
  disputeId,
  profile,
  allProfiles,
  onBack,
}) {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  // Directions state
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create Direction Form state
  const [directionName, setDirectionName] = useState("");
  const [directionTo, setDirectionTo] = useState([]);
  const [finalDate, setFinalDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Uploading state
  const [uploadingId, setUploadingId] = useState(null);

  // Pagination state (Client side since backend serves all results in results key)
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // Fetch directions list
  const fetchList = () => {
    setLoading(true);
    setError("");
    getDirections(disputeId)
      .then((res) => {
        const payload = res.data;
        if (payload && Array.isArray(payload.results)) {
          setDirections(payload.results);
        } else if (Array.isArray(payload)) {
          setDirections(payload);
        } else {
          setDirections([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching directions:", err);
        setError("Failed to load Directions by Tribunal.");
        setLoading(false);
      });
  };

  useEffect(() => {
    if (disputeId) {
      fetchList();
    }
  }, [disputeId]);

  // Form submit handler (Arbitrators only)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!directionName.trim()) {
      showSnackbar("Direction name is required.", "error");
      return;
    }
    if (!finalDate) {
      showSnackbar("Due date is required.", "error");
      return;
    }
    if (directionTo.length === 0) {
      showSnackbar("Please select at least one party.", "error");
      return;
    }

    setSubmitting(true);
    const payload = {
      direction_name: directionName.trim(),
      direction_to: directionTo,
      party_type: "",
      final_date: formatLocalDate(finalDate),
      dispute_id: disputeId,
    };

    try {
      await createDirection(payload);
      showSnackbar("Direction created successfully.", "success");
      setDirectionName("");
      setDirectionTo([]);
      setFinalDate(null);
      fetchList();
    } catch (err) {
      console.error("Error creating direction:", err);
      showSnackbar("Could not create direction. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Upload document handler
  const handleFileChange = async (directionId, filesList) => {
    if (!filesList || filesList.length === 0) return;
    const selectedFile = filesList[0];

    setUploadingId(directionId);
    try {
      await uploadDirectionDocument(directionId, selectedFile);
      showSnackbar("Document uploaded successfully.", "success");
      fetchList();
    } catch (err) {
      console.error("Error uploading document:", err);
      showSnackbar("Could not upload document. Please try again.", "error");
    } finally {
      setUploadingId(null);
    }
  };

  // Check upload capability based on role assignment
  const canUploadForDirection = (direction) => {
    // Arbitrators cannot upload
    if (profile.profile_type === "arbitrator_rv") return false;

    if (direction && Array.isArray(direction.direction_to) && direction.direction_to.length > 0) {
      const directionToIds = direction.direction_to.map((item) => {
        if (typeof item === "object" && item !== null) {
          return item.id || item.profile_id || item.profile || item.profile?.id;
        }
        return item;
      });
      return directionToIds.map(Number).includes(Number(profile.id));
    }

    const loggedInMember = allProfiles.find((p) => p.profile.id === profile.id);
    if (!loggedInMember) return false;

    const displayRole = loggedInMember.profile.profile_type;
    const targetParty = direction.to_party; // "claimant" or "respondent"

    if (targetParty === "claimant") {
      return (
        displayRole === "Claimant" || displayRole === "Claimant's Counsel"
      );
    }
    if (targetParty === "respondent") {
      return (
        displayRole === "Respondent" || displayRole === "Respondent's Counsel"
      );
    }
    return false;
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleDownload = (fileId, fileName) => {
    DownloadFile(fileId)
      .then((res) => {
        // Presigned URL from the document-management service — validate the
        // origin before handing it to the browser.
        const onError = (message) => showSnackbar(message, "error");
        if (fileName?.toLowerCase().endsWith(".pdf")) {
          openTrustedUrl(res.data.url, { onError, context: "direction document preview" });
        } else {
          navigateToTrustedUrl(res.data.url, { onError, context: "direction document download" });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const isArbitrator = profile.profile_type === "arbitrator_rv";

  // Slice directions for frontend pagination
  const paginatedDirections = directions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper className={classes.paperCard}>
      {/* Back button */}
      {/* <Button
        className={classes.backButton}
        onClick={onBack}
        startIcon={<KeyboardBackspace />}
      >
        Back to Dispute
      </Button> */}

      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: "1.5rem" }}>
        <Typography className={classes.heading}>
          Directions by Tribunal
        </Typography>
      </Box>

      {/* Arbitrator Input Form */}
      {isArbitrator && (
        <Paper className={classes.formContainer} elevation={0}>
          <Typography className={classes.formTitle}>
            New Direction to Parties
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Title"
                  required
                  value={directionName}
                  onChange={(e) => setDirectionName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="outlined" size="small" required>
                  <InputLabel id="direction-to-label">Direction To</InputLabel>
                  <Select
                    labelId="direction-to-label"
                    label="Direction To"
                    multiple
                    value={directionTo}
                    onChange={(e) => setDirectionTo(e.target.value)}
                    renderValue={(selected) => {
                      const directionParties = (allProfiles || [])
                        .filter(
                          (p) =>
                            p.profile.profile_type !== "Arbitrator" &&
                            p.profile.profile_type !== "Conciliator"
                        )
                        .map((p) => {
                          const name = [p.user.first_name, p.user.last_name].filter(Boolean).join(" ");
                          return {
                            value: p.profile.id,
                            label: `${name} (${p.profile.profile_type})`,
                          };
                        });
                      return (
                        <Box display="flex" style={{ gap: 4, overflowX: "auto", flexWrap: "nowrap", maxWidth: "100%" }}>
                          {selected.map((val) => {
                            const party = directionParties.find((p) => p.value === val);
                            return (
                              <Chip
                                key={val}
                                label={party ? party.label : val}
                                size="small"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                onDelete={() => {
                                  const newValue = selected.filter((v) => v !== val);
                                  setDirectionTo(newValue);
                                }}
                              />
                            );
                          })}
                        </Box>
                      );
                    }}
                  >
                    {(allProfiles || [])
                      .filter(
                        (p) =>
                          p.profile.profile_type !== "Arbitrator" &&
                          p.profile.profile_type !== "Conciliator"
                      )
                      .map((p) => {
                        const name = [p.user.first_name, p.user.last_name].filter(Boolean).join(" ");
                        const value = p.profile.id;
                        const label = `${name} (${p.profile.profile_type})`;
                        return (
                          <MenuItem key={value} value={value}>
                            <Checkbox
                              checked={directionTo.indexOf(value) > -1}
                              size="small"
                              color="primary"
                              style={{ padding: "0 8px 0 0" }}
                            />
                            {label}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <DatePickerACCB
                  label="Due Date"
                  value={finalDate}
                  onChange={(date) => setFinalDate(date)}
                  fullWidth
                  minDate={new Date()}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  // size="small"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={submitting}
                  className={classes.submitButton}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {/* Directions table list */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" style={{ padding: "2rem" }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography className={classes.errorText} align="center">
          {error}
        </Typography>
      ) : directions.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography variant="body1">No directions recorded yet.</Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" style={{ maxWidth: '100%', border: '1px solid #E5E5E5' }}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>S.No.</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Directions by Arbitrator</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Given To</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Date of Direction</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Uploaded Document</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Limitation</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Overdue by (Days)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDirections.map((direction, index) => {
                  const sNo = page * rowsPerPage + index + 1;
                  const limitation = getLimitationStatus(direction);
                  const overdueDays = getOverdueDays(direction);
                  const showUpload = canUploadForDirection(direction) && !direction.file_name;

                  return (
                    <TableRow key={direction.id} hover>
                      <TableCell className={classes.tableCell}>{sNo}</TableCell>
                      <TableCell className={classes.tableCell}>{direction.direction_name || "—"}</TableCell>
                      <TableCell className={classes.tableCell}>
                        {Array.isArray(direction.direction_to) && direction.direction_to.length > 0 ? (
                          direction.direction_to.map((idOrObj) => {
                            const profileId = typeof idOrObj === "object" && idOrObj !== null
                              ? idOrObj.id || idOrObj.profile_id || idOrObj.profile || idOrObj.profile?.id
                              : idOrObj;
                            const target = allProfiles.find((p) => Number(p.profile.id) === Number(profileId));
                            if (target) {
                              const name = [target.user.first_name, target.user.last_name].filter(Boolean).join(" ");
                              return `${name} (${target.profile.profile_type})`;
                            }
                            return `Profile #${profileId}`;
                          }).join(", ")
                        ) : (
                          <span style={{ textTransform: "capitalize" }}>
                            {direction.to_party || "—"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>{direction.created_at || "—"}</TableCell>
                      <TableCell className={classes.tableCell}>
                        {direction.file_name ? (
                          <span
                            className={classes.downloadLink}
                            onClick={() => handleDownload(direction?.file, direction.file_name)}
                          >
                            {/* <DescriptionIcon style={{ fontSize: "1rem" }} /> */}
                            {direction.file_name}
                          </span>
                        ) : showUpload ? (
                          <Box>
                            <input
                              type="file"
                              id={`upload-file-input-${direction.id}`}
                              style={{ display: "none" }}
                              onChange={(e) => handleFileChange(direction.id, e.target.files)}
                            />
                            <label htmlFor={`upload-file-input-${direction.id}`}>
                              <Button
                                variant="outlined"
                                color="primary"
                                component="span"
                                size="small"
                                className={classes.uploadButton}
                                // startIcon={<CloudUploadIcon style={{ fontSize: "1rem" }} />}
                                disabled={uploadingId === direction.id}
                              >
                                {uploadingId === direction.id ? "Uploading..." : "Upload"}
                              </Button>
                            </label>
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Chip
                          label={limitation}
                          size="small"
                          style={getLimitationChipStyle(limitation)}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>{overdueDays}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" style={{ marginTop: "1rem" }}>
            <TablePagination
              component="div"
              count={directions.length}
              page={page}
              onChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
            />
          </Box>
        </>
      )}
    </Paper>
  );
}
